import React, { FC, ReactElement, useCallback, useEffect, useRef } from "react";
import { StyleSheet, View, ScrollView, Animated, Platform } from "react-native";
import { useResponsive } from "../../hooks/useResponsive";
import Gap from "../Gap";
import variables from "../../styles/variables";
import Footer from "../Footer";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import Filter from "../Filters/Filter";
import { FilterProps } from "../Filters/Filter";
import RHeading, { RTextColors } from "../RHeading/RHeading";
import { useAtom, useSetAtom } from "jotai";
import {
  advertisingAtom,
  globalFullscreenAtom,
  appScrollRefAtom2,
  topOfPageRefAtom,
  pageDataAtom,
  tabBarHeightAtom,
} from "../../atoms";

import RText from "../RText";
import { useShowSubmenu } from "../../hooks/useShowSubmenu";
import { CenterAdvert } from "./CenterAdvert";
import { LoadingState } from "../States/states";
import lazyImport from "../../utils/lazyImport/lazyImport";
import { HeaderAdvert } from "./HeaderAdvert";
import { APP_ENV } from "../../client";
import { LambdatestScrollOverlay } from "../LambdatestScrollOverlay";
import CmsHeader from "./CmsHeader";
import RSuspense from "../../utils/RSuspense/RSuspense";

const SkyscraperAdvert = lazyImport(() => import("./SkyscraperAdvert"));

const { spacing, colors } = variables;

// handle sticky ad top margin on larger screens
const Wrapper = ({ children, footerHeight, noPadding }) => {
  const wrapperHeight = useRef(new Animated.Value(0)).current;
  const [adverts] = useAtom(advertisingAtom);
  const [isGlobalFullScreen] = useAtom(globalFullscreenAtom);
  const appScrollRef2 = useRef<ScrollView>(undefined);
  const [tabbarHeight] = useAtom(tabBarHeightAtom);

  const [, setAppScrollRef2] = useAtom(appScrollRefAtom2);
  const menu = useShowSubmenu();
  const {
    isXSmallOnly,
    isXSmallAndUp,
    isSmallAndUp,
    isMediumAndUp,
    isLargeAndUp,
    isXLargeAndUp,
  } = useResponsive();

  const gutterMargin = () => {
    if (noPadding) return 0;
    if (isXLargeAndUp) return spacing.medium;
    if (isMediumAndUp) return spacing.small;
    if (isSmallAndUp) return spacing.small;
    if (isXSmallAndUp || isXSmallOnly) return spacing.xsmall;
  };

  const adaptivePadding = { paddingHorizontal: gutterMargin() };
  const adRequestComplete = Object.keys(adverts || {}).length > 0;

  // set appScrollRef2 once its defined inside a useEffect
  useEffect(() => {
    if (Platform.OS == "web") return;
    if (appScrollRef2?.current) setAppScrollRef2(appScrollRef2);
  }, [appScrollRef2]);

  if (Platform.OS === "web") {
    return (
      <View
        onLayout={(e) => wrapperHeight.setValue(e.nativeEvent.layout.height)}
        style={[
          styles.container,
          styles.webContainer,
          menu ? { marginTop: 0 } : { marginTop: 50 },
          adaptivePadding,
          !isLargeAndUp && { marginBottom: 20 },
          isLargeAndUp && { minWidth: 1200, maxWidth: 1400 },
        ]}
      >
        <View style={[styles.contentContainerWeb]}>{children}</View>

        {!adRequestComplete || (adRequestComplete && adverts.skyscraper) ? (
          <RSuspense fallback={<View style={{ width: 250 }}></View>}>
            <SkyscraperAdvert
              ad={adverts?.skyscraper}
              containerHeight={wrapperHeight}
              footerHeight={footerHeight}
            />
          </RSuspense>
        ) : null}
      </View>
    );
  } else {
    const contentStyles = [styles.contentContainerMobile];

    isGlobalFullScreen
      ? contentStyles.push({ margin: 0 })
      : contentStyles.push({
          marginHorizontal: spacing.xsmall,
          paddingBottom: tabbarHeight + 20 || 20,
        });

    return (
      <ScrollView
        accessibilityLabel="Scroll Bar"
        style={[styles.container, styles.nativeContainer]}
        contentContainerStyle={contentStyles}
        ref={appScrollRef2}
        scrollEventThrottle={1600}
        overScrollMode="never"
        scrollEnabled={!isGlobalFullScreen}
      >
        {children}
        {!isGlobalFullScreen && (
          <RText
            size="xs"
            color="grey"
            family="secondary"
            style={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              height: 40,
              width: "100%",
              textAlign: "center",
              paddingVertical: 20,
              marginBottom: 20,
            }}
          >
            Copyright {new Date().getFullYear()} Racing TV - All Rights
            Reserved.
          </RText>
        )}

        {APP_ENV === "integration" && <LambdatestScrollOverlay />}
      </ScrollView>
    );
  }
};

type LayoutProps = {
  content?: ReactElement;
  heading?: string | React.ReactNode;
  headingComponent?: ReactElement;
  subHeading?: string | React.ReactNode;
  filter?: FilterProps;
  pagination?: React.ReactNode;
  noPadding?: boolean;
  headingLoader?: boolean;
};

export const Layout: FC<LayoutProps> = ({
  content,
  heading,
  headingComponent,
  subHeading,
  pagination = null,
  filter,
  noPadding = false,
  headingLoader = true,
}) => {
  // handle sticky ad top margin on larger screens
  const footerHeight = useRef(new Animated.Value(0)).current;
  const [isGlobalFullScreen] = useAtom(globalFullscreenAtom);
  const { isSmallAndDown, isMediumAndDown } = useResponsive();
  const [adverts] = useAtom(advertisingAtom);
  const topOfPageRef = useRef<View>(null);
  const setTopOfPageRef = useSetAtom(topOfPageRefAtom);
  const [pageData] = useAtom(pageDataAtom);

  useEffect(() => {
    setTopOfPageRef(topOfPageRef);
  }, [setTopOfPageRef, topOfPageRef]);

  const isPageDataUndefined = typeof pageData === "undefined";

  return (
    <>
      <Wrapper footerHeight={footerHeight} noPadding={noPadding}>
        <View ref={topOfPageRef} collapsable={false}></View>
        <HeaderAdvert noPadding={noPadding} />
        <Breadcrumbs />
        <View>
          <View>
            {heading && !isGlobalFullScreen ? (
              <View style={[subHeading && styles.subHeading]}>
                <View
                  style={[
                    {
                      flexDirection: "row",
                      justifyContent: "space-between",
                    },
                    filter && isMediumAndDown ? { maxWidth: "60%" } : {},
                  ]}
                >
                  <CmsHeader
                    showLoader={isPageDataUndefined}
                    fallback={heading}
                    cmsTitle={pageData?.page?.h1}
                    level="1"
                  />
                  {filter && filter.type != "dropdown" ? (
                    <Filter filter={filter} />
                  ) : null}
                </View>
                <Gap vertical size="xsmall" />

                {!!subHeading && (
                  <>
                    <CmsHeader
                      showLoader={isPageDataUndefined}
                      fallback={subHeading}
                      cmsTitle={pageData?.page?.h2}
                      level="2"
                      color="grey"
                    />
                    <Gap vertical size="xsmall" />
                  </>
                )}

                {pageData?.page?.h3 ? (
                  <>
                    <RHeading level="3">{pageData.page.h3}</RHeading>
                    <Gap vertical size="xsmall" />
                  </>
                ) : null}
              </View>
            ) : null}

            {headingComponent && !isGlobalFullScreen && (
              <View style={{ marginBottom: 20 }}>
                {headingComponent}
                {filter && filter.type != "dropdown" && (
                  <Filter filter={filter} />
                )}
              </View>
            )}

            {!heading &&
              !headingComponent &&
              headingLoader &&
              !isGlobalFullScreen && <LoadingState name="HeadingLoader" />}
          </View>

          {content}

          {!isGlobalFullScreen && filter && filter.type == "dropdown" ? (
            <Filter filter={filter} />
          ) : null}
        </View>

        {/* footer ad */}
        {!isGlobalFullScreen && adverts?.footer ? (
          <>
            <Gap vertical />
            <CenterAdvert ad={adverts.footer} />
          </>
        ) : (
          <Gap />
        )}
        {pagination && !isGlobalFullScreen && pagination}
        {Platform.OS == "web" && !isGlobalFullScreen ? (
          <RText
            size="xs"
            color="grey"
            family="secondary"
            style={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              height: 30,
              paddingTop: 10,
            }}
          >
            Copyright {new Date().getFullYear()} Racing TV - All Rights
            Reserved.
          </RText>
        ) : null}
      </Wrapper>

      {!isGlobalFullScreen && <Footer ref={footerHeight} />}
      {Platform.OS === "web" && APP_ENV === "integration" && (
        <LambdatestScrollOverlay />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    backgroundColor: colors.palette.rtv.mainBackground,
  },
  nativeContainer: {
    flexGrow: 1,

    flex: 1,
  },
  webContainer: {
    flexDirection: "row",
    height: "auto",
    minHeight: "70%",
    alignContent: "center",
    justifyContent: "center",
    borderRadius: 10,
    margin: "auto",
    marginBottom: 50, // height of sub menu
  },
  contentContainerWeb: {
    flex: 1,
    maxWidth: variables.layoutMaxWidth,
  },
  contentContainerMobile: {
    flexGrow: 1,
  },
});

export default Layout;
